import React, {useRef, useState} from "react";
import {IoMdCheckmark, IoMdClose} from "react-icons/io";
import {AssociationRead, TagRead, TagType} from "../../schemas";
import {getRelevanceScore, RelevanceLabel, getRelevanceLevelFromScore} from "../../routines/schemas";
import {applyTag} from "../../api";
import {useGroupContext} from "../../contexts/group_context";
import ReactDOM from "react-dom";
import {PiBuildings, PiMapPin, PiUser, PiPackage, PiHash, PiCalendarBlank} from "react-icons/pi";
import {EntityType} from "../../routines/schemas";

import {TaggingConfidenceColors} from "../../colors";
import {AllowConfirmationOfCodes} from "../../feature_flags";
import {withApiErrorHandler} from "../../error_handling";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";

// ---------- Display size: adapts badges based on visible count ----------
type DisplaySize = 'expanded' | 'medium' | 'compact';

function getDisplaySize(count: number): DisplaySize {
    if (count <= 2) return 'expanded';
    if (count <= 4) return 'medium';
    return 'compact';
}

// ---------- Entity styling ----------
const entityColorMap: Record<EntityType, string> = {
    [EntityType.organization]: "bg-emerald-50 text-emerald-800 border-emerald-200",
    [EntityType.location]: "bg-amber-50 text-amber-800 border-amber-200",
    [EntityType.item]: "bg-violet-50 text-violet-800 border-violet-200",
    [EntityType.topic]: "bg-cyan-50 text-cyan-800 border-cyan-200",
    [EntityType.person]: "bg-indigo-50 text-indigo-800 border-indigo-200",
    [EntityType.event]: "bg-slate-50 text-slate-800 border-slate-200",
};

const entityIconColor: Record<EntityType, string> = {
    [EntityType.organization]: "text-emerald-600",
    [EntityType.location]: "text-amber-600",
    [EntityType.item]: "text-violet-600",
    [EntityType.topic]: "text-cyan-600",
    [EntityType.person]: "text-indigo-600",
    [EntityType.event]: "text-slate-600",
};

const entityTypeLabel: Record<EntityType, string> = {
    [EntityType.organization]: "Organization",
    [EntityType.location]: "Location",
    [EntityType.item]: "Item",
    [EntityType.topic]: "Topic",
    [EntityType.person]: "Person",
    [EntityType.event]: "Event",
};

const EntityIcon: React.FC<{ type: EntityType; className?: string }> = ({type, className = "w-3 h-3"}) => {
    const color = entityIconColor[type] ?? "";
    const cls = `${className} ${color}`;
    switch (type) {
        case EntityType.organization: return <PiBuildings className={cls}/>;
        case EntityType.location:     return <PiMapPin className={cls}/>;
        case EntityType.item:         return <PiPackage className={cls}/>;
        case EntityType.topic:        return <PiHash className={cls}/>;
        case EntityType.person:       return <PiUser className={cls}/>;
        case EntityType.event:        return <PiCalendarBlank className={cls}/>;
        default:                      return null;
    }
};


// ---------- Portal-based tooltip (escapes container clipping) ----------
const Tooltip: React.FC<{ children: React.ReactNode; text?: string; content?: React.ReactNode }> = ({children, text, content}) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({top: 0, left: 0});
    const ref = useRef<HTMLElement>(null);

    const show = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setCoords({top: rect.top, left: rect.left + rect.width / 2});
            setVisible(true);
        }
    };
    const hide = () => setVisible(false);

    return (
        <>
            <span
                ref={ref}
                onMouseEnter={show}
                onMouseLeave={hide}
                style={{display: "inline-block"}}
            >
                {children}
            </span>
            {visible &&
                ReactDOM.createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: coords.top - 10,
                            left: coords.left,
                            transform: "translate(-50%, -100%)",
                            zIndex: 1000,
                            maxWidth: 320,
                        }}
                        className="bg-gray-900 text-white text-sm rounded-lg px-3 py-2 shadow-xl"
                    >
                        {content ?? text}
                    </div>,
                    document.body
                )}
        </>
    );
};


// ---------- Score badge / pill ----------
interface PillScoreButtonProps {
    score: number;
    labeled: boolean;
    loading?: boolean;
    association: AssociationRead;
    tag: TagRead;
    tagType?: TagType;
    displaySize: DisplaySize;
    onConfirm: (assoc: AssociationRead) => void;
    onReject: (assoc: AssociationRead) => void;
}

const PillScoreButton: React.FC<PillScoreButtonProps> = ({
    labeled,
    loading = false,
    association,
    tag,
    tagType,
    displaySize,
    onConfirm,
    onReject,
}) => {
    const pct = ((association.score! * 100).toFixed(0)) + "%";
    const isEntity = tagType === TagType.ENTITY;
    const entityType = tag.entity_type as EntityType | undefined;
    const fill = TaggingConfidenceColors.fromScore(association.score!);

    // Loading state
    if (loading) {
        return (
            <div className={`inline-flex items-center rounded-lg border border-gray-200/60 bg-white shadow-sm ${
                displaySize === 'expanded' ? 'p-3 w-full' : displaySize === 'medium' ? 'px-3 py-1.5' : 'px-2 py-1'
            }`}>
                <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"/>
            </div>
        );
    }

    // ── Entity rendering (adapts to display size) ──
    if (isEntity && entityType && entityColorMap[entityType]) {
        const colors = entityColorMap[entityType];

        if (displaySize === 'expanded') {
            return (
                <div className={`flex items-start gap-2.5 p-2.5 rounded-xl border shadow-sm hover:shadow transition-all duration-200 w-full ${colors}`}>
                    <EntityIcon type={entityType} className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-tight">{tag.tag_name}</div>
                        <div className="text-[11px] opacity-60 mt-0.5">{entityTypeLabel[entityType]}</div>
                        {tag.description && (
                            <div className="text-xs opacity-50 mt-1 line-clamp-2">{tag.description}</div>
                        )}
                    </div>
                </div>
            );
        }

        if (displaySize === 'medium') {
            return (
                <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border shadow-sm hover:shadow transition-all duration-150 ${colors}`}
                    title={tag.description || tag.tag_name}
                >
                    <EntityIcon type={entityType} className="w-3.5 h-3.5"/>
                    <span>{tag.tag_name.length > 24 ? tag.tag_name.slice(0, 21) + "..." : tag.tag_name}</span>
                </span>
            );
        }

        // Compact
        return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border shadow-sm ${colors}`}
                title={tag.description || tag.tag_name}
            >
                <EntityIcon type={entityType} className="w-3 h-3"/>
                <span>{tag.tag_name.length > 20 ? tag.tag_name.slice(0, 17) + "..." : tag.tag_name}</span>
            </span>
        );
    }

    // ── Non-entity: Expanded (1-2 badges) ──
    if (displaySize === 'expanded') {
        const level = getRelevanceLevelFromScore(association.score!);
        return (
            <div
                className="flex flex-col gap-1.5 p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-all duration-200 w-full"
                style={{borderLeft: `3px solid ${fill}`}}
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <Tooltip content={
                            <div>
                                <div className="font-semibold mb-1">{tag.tag_name}</div>
                                {tag.description && <div className="text-gray-300 text-xs leading-relaxed">{tag.description}</div>}
                                <div className="text-gray-400 text-xs mt-1">{pct} relevance</div>
                            </div>
                        }>
                            <span className="text-sm font-semibold text-gray-900 leading-tight">{tag.tag_name}</span>
                        </Tooltip>
                        <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
                            style={{backgroundColor: fill}}
                        >
                            {level}
                        </span>
                        {labeled && <IoMdCheckmark className="w-3 h-3 text-green-500 flex-shrink-0" title="Confirmed"/>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {AllowConfirmationOfCodes && (
                            <Tooltip text="Confirm relevance">
                                <button
                                    onClick={() => onConfirm(association)}
                                    className="p-1 rounded-md bg-gray-50 hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                                >
                                    <IoMdCheckmark className="w-3.5 h-3.5"/>
                                </button>
                            </Tooltip>
                        )}
                        <Tooltip text="Remove">
                            <button
                                onClick={() => onReject(association)}
                                className="p-1 rounded-md bg-gray-50 hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <IoMdClose className="w-3.5 h-3.5"/>
                            </button>
                        </Tooltip>
                    </div>
                </div>
                {tag.description && (
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{tag.description}</p>
                )}
            </div>
        );
    }

    // ── Non-entity: Medium (3-4 badges) ──
    if (displaySize === 'medium') {
        return (
            <div
                className="inline-flex items-center gap-1.5 leading-none mx-0.5 my-0.5 px-2 py-1 rounded-lg bg-white shadow-sm hover:shadow transition-all duration-150"
                style={{borderLeft: `3px solid ${fill}`}}
            >
                <Tooltip content={
                    <div>
                        <div className="font-semibold mb-1">{tag.tag_name}</div>
                        {tag.description && <div className="text-gray-300 text-xs leading-relaxed">{tag.description}</div>}
                        <div className="text-gray-400 text-xs mt-1">{pct} relevance</div>
                    </div>
                }>
                    <span className="text-xs font-medium text-gray-800">
                        {tag.tag_name.length > 24 ? tag.tag_name.slice(0, 21) + "..." : tag.tag_name}
                    </span>
                </Tooltip>

                <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white tabular-nums flex-shrink-0"
                    style={{backgroundColor: fill}}
                >
                    {pct}
                </span>

                <div className="flex items-center gap-0.5">
                    {AllowConfirmationOfCodes && (
                        <Tooltip text="Confirm">
                            <button
                                onClick={() => onConfirm(association)}
                                className="rounded-full hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                            >
                                <IoMdCheckmark className="w-3 h-3"/>
                            </button>
                        </Tooltip>
                    )}
                    <Tooltip text="Remove">
                        <button
                            onClick={() => onReject(association)}
                            className="rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                            <IoMdClose className="w-3 h-3"/>
                        </button>
                    </Tooltip>
                </div>
            </div>
        );
    }

    // ── Non-entity: Compact (5+ badges) ──
    return (
        <div
            className="inline-flex items-center gap-1 leading-none mx-0.5 my-0.5 pl-2 pr-1.5 py-0.5 rounded-lg bg-white shadow-sm hover:shadow transition-all duration-150"
            style={{borderLeft: `3px solid ${fill}`}}
        >
            <Tooltip content={
                <div>
                    <div className="font-semibold mb-1">{tag.tag_name}</div>
                    {tag.description && <div className="text-gray-300 text-xs leading-relaxed">{tag.description}</div>}
                    <div className="text-gray-400 text-xs mt-1">{pct} relevance</div>
                </div>
            }>
                <span className="text-xs font-medium text-gray-800 select-none">
                    {tag.tag_name.length > 18 ? tag.tag_name.slice(0, 15) + "..." : tag.tag_name}
                </span>
            </Tooltip>

            <div className="flex items-center gap-0.5">
                {AllowConfirmationOfCodes && (
                    <Tooltip text="Confirm">
                        <button
                            onClick={() => onConfirm(association)}
                            className="rounded-full hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                        >
                            <IoMdCheckmark className="w-3 h-3"/>
                        </button>
                    </Tooltip>
                )}
                <Tooltip text="Remove">
                    <button
                        onClick={() => onReject(association)}
                        className="rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    >
                        <IoMdClose className="w-3 h-3"/>
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};


// ---------- Container component ----------
interface ResponsiveToolBarProps {
    updateParent: () => void;
    filteredTagMap: Map<string, TagRead>;
    filteredAssociationMap: Map<string, AssociationRead>;
    tagType?: TagType;
}

const DocumentToolsCodes: React.FC<ResponsiveToolBarProps> = ({
    updateParent,
    filteredTagMap,
    filteredAssociationMap,
    tagType,
}) => {
    const {currentGroup} = useGroupContext()!;
    const {navigateTo} = useNavigationContext()!;

    const displaySize = getDisplaySize(filteredAssociationMap.size);

    const handleWrongLabelConfirmation = async (association: AssociationRead) => {
        const operatorLabeled = true;
        const score = getRelevanceScore(RelevanceLabel.NOT_RELEVANT);
        await withApiErrorHandler(
            () => applyTag(association.tag_id, operatorLabeled, score, association.doc_id, currentGroup!),
            {titleQualifier: "Error Updating Tag", navigateTo: navigateTo});
        updateParent();
    };

    const handleCorrectLabelConfirmation = async (association: AssociationRead) => {
        const operatorLabeled = true;
        await withApiErrorHandler(
            () => applyTag(association.tag_id, operatorLabeled, association.score!, association.doc_id, currentGroup!),
            {titleQualifier: "Error Updating Tag", navigateTo: navigateTo});
        updateParent();
    };

    const getCodeLabels = () => {
        const elements: JSX.Element[] = [];
        for (const association of filteredAssociationMap.values()) {
            const tag = filteredTagMap.get(association.tag_id);
            if (!tag || association.score === null || association.score === undefined) continue;

            elements.push(
                <PillScoreButton
                    key={association.tag_id}
                    score={association.score}
                    labeled={association.operator_labeled}
                    loading={false}
                    tag={tag}
                    tagType={tagType}
                    displaySize={displaySize}
                    onConfirm={handleCorrectLabelConfirmation}
                    onReject={handleWrongLabelConfirmation}
                    association={association}
                />
            );
        }
        return elements;
    };

    const containerClass = displaySize === 'expanded'
        ? 'flex flex-col gap-2 w-full'
        : displaySize === 'medium'
            ? 'flex flex-wrap justify-center items-start gap-1.5'
            : 'flex flex-wrap justify-center items-start gap-1';

    return (
        <div className={containerClass}>
            {getCodeLabels()}
        </div>
    );
};

export default DocumentToolsCodes;
